import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { Map } from "maplibre-gl";
import { FeatureCollection, Geometry } from "geojson";

import { COUNTRY_SOURCE_ID } from "@/constants/map";
import { RootState } from "@/store";
import { setLegendFilters, setSelectedFeature } from "@/store/slice/mapSlice";
import { setLoadingOverlay } from "@/store/slice/uiSlice";
import { EspatialAggregationType } from "@/types/common";
import { Dataset } from "@/types/dataset";
import { LayerType, SelectedFeature } from "@/types/map";
import { AreaInputType, EOutputAnalysisType } from "@/types/spatial";
import {
  convertDatasetResponseToFeatureCollection,
  getAvailableDatasets
} from "@/utils/dataset";
import { removeAllSourceLayerWithPrefix } from "@/utils/map";

export type UseMapLayerInitializationProps = {
  map: Map;
  isMapLoaded: boolean;
  handleAddLineLayer: (
    dataset: Dataset,
    featureData: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddPolygonLayer: (
    dataset: Dataset,
    featureData: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddPointsLayer: (
    dataset: Dataset,
    featureData: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddH3GeohashLayer: (
    dataset: Dataset,
    featureData: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddSiteProfiling: (
    newDataset: Dataset,
    featureCollection: FeatureCollection<Geometry>
  ) => void;
  handleClearAnalysisResultView: () => void;
  handleClearSelectedState: (feature: SelectedFeature) => void;
  removeCatchmentMapDisplay: () => void;
};

export const useMapLayerInitialization = ({
  map,
  isMapLoaded,
  handleAddLineLayer,
  handleAddPolygonLayer,
  handleAddPointsLayer,
  handleAddSiteProfiling,
  handleClearAnalysisResultView,
  removeCatchmentMapDisplay,
  handleAddH3GeohashLayer
}: UseMapLayerInitializationProps) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const { visualizationOrder, datasets, rawDatasets } = useSelector(
    (state: RootState) => state.dataset
  );

  const { comparisonFeatureData } = useSelector(
    (state: RootState) => state.map
  );

  const {
    spatialConfig: { areaInput: spatialAreaInputType }
  } = useSelector((state: RootState) => state.spatial);

  const {
    showAnalysisResultPopup,
    showAnalysisSummaryPopup,
    showComparisonPopup,
    showSearchLocationPopup
  } = useSelector((state: RootState) => state.ui);

  const availableDatasets = getAvailableDatasets(datasets);
  const isPreviewMode = searchParams.get("preview") === "true";

  // Check if display as preview mode, will clear catchment and admin area
  // This useEffect is use to remove layer for spatialAreaInputType
  useEffect(() => {
    if (
      spatialAreaInputType?.value === AreaInputType.ADMIN_AREA ||
      isPreviewMode
    )
      removeCatchmentMapDisplay();
    if (
      spatialAreaInputType?.value === AreaInputType.CATCHMENT ||
      isPreviewMode
    )
      removeAllSourceLayerWithPrefix(map, COUNTRY_SOURCE_ID, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spatialAreaInputType, map, isPreviewMode]);

  // This effect is to add map layers for each available dataset when the map instance has been loaded
  useEffect(() => {
    const processLayers = () => {
      // Check if map is loaded and not showing search location popup
      if (isMapLoaded && !showSearchLocationPopup) {
        for (let i = visualizationOrder.length - 1; i >= 0; i--) {
          const datasetId = visualizationOrder[i];
          /** Flag to check if the specified `datasetId` is of a dataset that's still available i.e. not yet deleted */
          const isDatasetIdAvailable = Object.prototype.hasOwnProperty.call(
            availableDatasets,
            datasetId
          );

          if (isDatasetIdAvailable) {
            const dataset = availableDatasets[datasetId];
            const data = rawDatasets[datasetId];
            const featureCollection = convertDatasetResponseToFeatureCollection(
              data,
              showAnalysisResultPopup || showAnalysisSummaryPopup
                ? undefined
                : dataset.categories
            );
            const spatialType = dataset.properties.spatialType;

            switch (spatialType) {
              case LayerType.POLYGON:
              case LayerType.ADMINISTRATIVE:
              case EOutputAnalysisType.GRID:
                handleAddPolygonLayer(dataset, featureCollection);
                break;
              case LayerType.POINT:
                handleAddPointsLayer(dataset, featureCollection);
                break;
              case EOutputAnalysisType.PROFILING:
                handleAddSiteProfiling(dataset, featureCollection);
                break;
              case EspatialAggregationType.H3:
              case EspatialAggregationType.GEOHASH:
                handleAddH3GeohashLayer(dataset, featureCollection);
                break;
              case LayerType.LINE:
                handleAddLineLayer(dataset, featureCollection);
                break;
              default:
                break;
            }
          }
        }
      }
    };

    processLayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapLoaded, showSearchLocationPopup]);

  // This useEffect is used to highlight selected feature when map is loaded and in comparison mode
  useEffect(() => {
    if (showComparisonPopup.isShow && isMapLoaded && map) {
      comparisonFeatureData.forEach((feature) => {
        if (!map.getSource(feature.source as string)) return;
        map.setFeatureState(
          { source: feature.source as string, id: feature.id },
          { selected: true }
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showComparisonPopup.isShow, comparisonFeatureData, isMapLoaded]);

  // This useEffect is used to clear selected feature when browser refreshes
  useEffect(() => {
    return () => {
      dispatch(setLoadingOverlay(false));
      dispatch(setSelectedFeature(null));
      dispatch(setLegendFilters(undefined));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This useEffect is used to clear all layers related to catchment when in analysis result popup is open
  useEffect(() => {
    if (showAnalysisResultPopup) handleClearAnalysisResultView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAnalysisResultPopup]);
};
